import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus, FaSearch, FaSpinner } from "react-icons/fa";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";

export interface Goalscorer {
  team: "A" | "B";
  playerId: number;
}

export interface MatchFormData {
  date: string;
  teamA: number[];
  teamB: number[];
  /** Ephemeral guest temp IDs (negative) → display name for persistence on submit. */
  guestPlayerNames?: Record<number, string>;
  scoreA: string | number;
  scoreB: string | number;
  winner?: "A" | "B" | "Draw";
  goalscorers: Goalscorer[];
  youtubeLink: string;
  autoScore: boolean;
}

export interface PlayerWithGames {
  id: number;
  name: string;
  gamesPlayed: number;
  isGuest?: boolean;
}

export const defaultMatchFormData: MatchFormData = {
  date: new Date().toISOString().slice(0, 10),
  teamA: [],
  teamB: [],
  scoreA: "",
  scoreB: "",
  winner: undefined,
  goalscorers: [],
  youtubeLink: "",
  autoScore: false,
};

const STEPS = ["date", "teams", "score", "extras"] as const;
type Step = (typeof STEPS)[number];

/** Prevent global Input focus scale from breaking modal layout. */
const MODAL_FIELD_CLASS =
  "focus-visible:scale-100 hover:shadow-none focus-visible:ring-offset-0";

const MODAL_CHECKBOX_CLASS = "focus-visible:ring-offset-0";

const EMPTY_EVENT_GUESTS: { name: string }[] = [];

export interface MatchFormWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  players: PlayerWithGames[];
  /** Guests from event RSVPs (not in roster) — selectable in team step. */
  eventGuests?: { name: string }[];
  initialData?: MatchFormData;
  mode: "create" | "edit";
  isProcessing?: boolean;
  prefillTeams?: { teamA: number[]; teamB: number[] };
  onSubmit: (data: MatchFormData) => void;
}

function buildEventGuestEntries(
  eventGuests: { name: string }[],
  rosterNames: Set<string>
): { tempId: number; name: string }[] {
  const seen = new Set<string>();
  const entries: { tempId: number; name: string }[] = [];
  let nextId = -1;

  for (const guest of eventGuests) {
    const name = guest.name.trim();
    if (name.length < 2) continue;
    const key = name.toLowerCase();
    if (rosterNames.has(key) || seen.has(key)) continue;
    seen.add(key);
    entries.push({ tempId: nextId, name });
    nextId -= 1;
  }

  return entries;
}

export function MatchFormWizard({
  open,
  onOpenChange,
  players,
  eventGuests = EMPTY_EVENT_GUESTS,
  initialData,
  mode,
  isProcessing = false,
  prefillTeams,
  onSubmit,
}: MatchFormWizardProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [step, setStep] = useState<Step>("date");
  const [formData, setFormData] = useState<MatchFormData>(defaultMatchFormData);
  const [playerSearch, setPlayerSearch] = useState("");
  const [addedGuests, setAddedGuests] = useState<{ tempId: number; name: string }[]>([]);
  const [newGuestName, setNewGuestName] = useState("");

  const rosterNameKeys = useMemo(
    () => new Set(players.map((p) => p.name.trim().toLowerCase())),
    [players]
  );

  const eventGuestEntries = useMemo(
    () => buildEventGuestEntries(eventGuests, rosterNameKeys),
    [eventGuests, rosterNameKeys]
  );

  useEffect(() => {
    if (!open) return;

    const base = initialData ?? defaultMatchFormData;
    const guests = buildEventGuestEntries(eventGuests, rosterNameKeys);
    const guestPlayerNames: Record<number, string> = { ...(base.guestPlayerNames ?? {}) };
    for (const guest of guests) {
      guestPlayerNames[guest.tempId] = guest.name;
    }

    setFormData(
      prefillTeams && mode === "create"
        ? {
            ...base,
            teamA: prefillTeams.teamA,
            teamB: prefillTeams.teamB,
            guestPlayerNames,
          }
        : { ...base, guestPlayerNames }
    );
    setStep("date");
    setPlayerSearch("");
    setAddedGuests([]);
    setNewGuestName("");
  }, [open, initialData, prefillTeams, mode, eventGuests, rosterNameKeys]);

  const stepIndex = STEPS.indexOf(step);
  const stepLabels = [
    t("matches.wizardStepDate", "Date"),
    t("matches.wizardStepTeams", "Teams"),
    t("matches.wizardStepScore", "Score"),
    t("matches.wizardStepExtras", "Extras"),
  ];

  const allSelectablePlayers = useMemo(() => {
    const guestEntries = [...eventGuestEntries, ...addedGuests];
    const guestPlayers: PlayerWithGames[] = guestEntries.map((guest) => ({
      id: guest.tempId,
      name: guest.name,
      gamesPlayed: 0,
      isGuest: true,
    }));
    return [...players, ...guestPlayers];
  }, [players, eventGuestEntries, addedGuests]);

  const filteredModalPlayers = useMemo(() => {
    if (!playerSearch.trim()) return allSelectablePlayers;
    const searchTerms = playerSearch
      .split(",")
      .map((term) => term.trim().toLowerCase())
      .filter((term) => term.length > 0);
    return allSelectablePlayers.filter((player) =>
      searchTerms.some((term) => player.name.toLowerCase().includes(term))
    );
  }, [allSelectablePlayers, playerSearch]);

  const handleAddGuest = () => {
    const name = newGuestName.trim();
    if (name.length < 2) return;

    const key = name.toLowerCase();
    if (rosterNameKeys.has(key)) return;

    const existingGuest = [...eventGuestEntries, ...addedGuests].find(
      (g) => g.name.toLowerCase() === key
    );
    if (existingGuest) {
      setNewGuestName("");
      return;
    }

    const allGuestIds = [...eventGuestEntries, ...addedGuests].map((g) => g.tempId);
    const nextId = (allGuestIds.length ? Math.min(...allGuestIds) : 0) - 1;

    setAddedGuests((prev) => [...prev, { tempId: nextId, name }]);
    setFormData((prev) => ({
      ...prev,
      guestPlayerNames: { ...(prev.guestPlayerNames ?? {}), [nextId]: name },
    }));
    setNewGuestName("");
  };

  const isStepValid = (currentStep: Step): boolean => {
    switch (currentStep) {
      case "date":
        return !!formData.date;
      case "teams":
        return formData.teamA.length >= 1 && formData.teamB.length >= 1;
      case "score":
      case "extras":
        return true;
      default:
        return false;
    }
  };

  const isFormValid = useMemo(() => {
    if (!formData.date) return false;
    const hasScore = formData.scoreA !== "" && formData.scoreB !== "";
    const hasWinner =
      formData.winner === "A" || formData.winner === "B" || formData.winner === "Draw";
    const hasGoalscorers = (formData.goalscorers?.length || 0) > 0;
    return formData.teamA.length >= 1 && formData.teamB.length >= 1 && (hasScore || hasWinner || hasGoalscorers);
  }, [formData]);

  const handleTogglePlayerSelection = (teamKey: "teamA" | "teamB", playerId: number) => {
    const currentTeam = formData[teamKey];
    const otherTeamKey = teamKey === "teamA" ? "teamB" : "teamA";
    const otherTeam = formData[otherTeamKey];
    let newTeam;
    let newOtherTeam = [...otherTeam];
    let newGoalscorers = [...formData.goalscorers];

    if (currentTeam.includes(playerId)) {
      newTeam = currentTeam.filter((id) => id !== playerId);
      newGoalscorers = newGoalscorers.filter(
        (g) => !(g.team === teamKey.slice(-1).toUpperCase() && g.playerId === playerId)
      );
    } else {
      newTeam = [...currentTeam, playerId];
      if (otherTeam.includes(playerId)) {
        newOtherTeam = otherTeam.filter((id) => id !== playerId);
        newGoalscorers = newGoalscorers.filter(
          (g) => !(g.team === otherTeamKey.slice(-1).toUpperCase() && g.playerId === playerId)
        );
      }
    }

    setFormData({
      ...formData,
      [teamKey]: newTeam,
      [otherTeamKey]: newOtherTeam,
      goalscorers: newGoalscorers,
    });
  };

  const handleGoalscorerChange = (team: "A" | "B", playerId: number, increment: boolean) => {
    const currentGoalscorers = formData.goalscorers || [];
    if (increment) {
      setFormData({
        ...formData,
        goalscorers: [...currentGoalscorers, { team, playerId }],
        ...(formData.autoScore
          ? {
              scoreA: team === "A" ? (Number(formData.scoreA) || 0) + 1 : formData.scoreA,
              scoreB: team === "B" ? (Number(formData.scoreB) || 0) + 1 : formData.scoreB,
            }
          : {}),
      });
    } else {
      const indexToRemove = currentGoalscorers.findIndex(
        (g) => g.team === team && g.playerId === playerId
      );
      if (indexToRemove > -1) {
        const updatedGoalscorers = [...currentGoalscorers];
        updatedGoalscorers.splice(indexToRemove, 1);
        setFormData({
          ...formData,
          goalscorers: updatedGoalscorers,
          ...(formData.autoScore
            ? {
                scoreA:
                  team === "A" ? Math.max(0, (Number(formData.scoreA) || 0) - 1) : formData.scoreA,
                scoreB:
                  team === "B" ? Math.max(0, (Number(formData.scoreB) || 0) - 1) : formData.scoreB,
              }
            : {}),
        });
      }
    }
  };

  const getGoalscorerCount = (team: "A" | "B", playerId: number): number =>
    formData.goalscorers.filter((g) => g.team === team && g.playerId === playerId).length;

  const goNext = () => {
    if (!isStepValid(step)) return;
    const next = STEPS[stepIndex + 1];
    if (next) setStep(next);
  };

  const goBack = () => {
    const prev = STEPS[stepIndex - 1];
    if (prev) setStep(prev);
  };

  const handleSubmit = () => {
    if (!isFormValid) return;
    const guestPlayerNames: Record<number, string> = { ...(formData.guestPlayerNames ?? {}) };
    for (const guest of [...eventGuestEntries, ...addedGuests]) {
      guestPlayerNames[guest.tempId] = guest.name;
    }
    onSubmit({ ...formData, guestPlayerNames });
  };

  const stepIndicator = (
    <ol className="flex gap-1 mb-4" aria-label={t("matches.wizardProgress", "Match form progress")}>
      {STEPS.map((s, i) => (
        <li
          key={s}
          className={cn(
            "flex-1 h-1.5 rounded-full transition-colors",
            i <= stepIndex ? "bg-soccer-primary" : "bg-muted"
          )}
          aria-current={s === step ? "step" : undefined}
          title={stepLabels[i]}
        />
      ))}
    </ol>
  );

  const stepContent = (
    <div className="grid min-h-0 flex-1 gap-6 overflow-y-auto py-2">
      {step === "date" && (
        <div className="space-y-4">
          <Label htmlFor="matchDate">{t("matches.date")}</Label>
          <Input
            id="matchDate"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            disabled={isProcessing}
            className={MODAL_FIELD_CLASS}
          />
        </div>
      )}

      {step === "teams" && (
        <>
          <div className="relative w-full">
            <FaSearch className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("matches.searchPlayers", "Search players (e.g., Player1, Player2)")}
              value={playerSearch}
              onChange={(e) => setPlayerSearch(e.target.value)}
              className={cn("pl-8", MODAL_FIELD_CLASS)}
            />
          </div>
          <div className="flex gap-2">
            <Input
              placeholder={t("matches.guestNamePlaceholder", "Guest name")}
              value={newGuestName}
              onChange={(e) => setNewGuestName(e.target.value)}
              disabled={isProcessing}
              className={MODAL_FIELD_CLASS}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddGuest();
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleAddGuest}
              disabled={isProcessing || newGuestName.trim().length < 2}
              className="shrink-0 gap-1"
            >
              <FaPlus className="h-3 w-3" />
              {t("matches.addGuest", "Add guest")}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            {t(
              "matches.addGuestHint",
              "Guests are not in the squad — they are saved for this match only."
            )}
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-4">
            {(["teamA", "teamB"] as const).map((teamKey) => (
              <div key={teamKey} className="min-w-0 space-y-2">
                <Label className="font-semibold">
                  {teamKey === "teamA" ? t("matches.teamA") : t("matches.teamB")}
                </Label>
                <div className="max-h-[200px] space-y-0.5 overflow-y-auto rounded-md border p-2">
                  {filteredModalPlayers.map((player) => (
                    <div
                      key={`${teamKey}-${player.id}`}
                      className="flex min-h-9 items-center gap-2 rounded-sm px-1"
                    >
                      <Checkbox
                        id={`${teamKey}-${player.id}`}
                        checked={formData[teamKey].includes(player.id)}
                        onCheckedChange={() => handleTogglePlayerSelection(teamKey, player.id)}
                        disabled={isProcessing}
                        className={MODAL_CHECKBOX_CLASS}
                      />
                      <label
                        htmlFor={`${teamKey}-${player.id}`}
                        className="min-w-0 flex-1 cursor-pointer text-sm leading-tight"
                      >
                        <span className="block truncate">{player.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {player.isGuest
                            ? `(${t("matches.guestLabel", "Guest")})`
                            : `(${player.gamesPlayed} ${
                                player.gamesPlayed === 1
                                  ? t("matches.game", "game")
                                  : t("matches.games", "games")
                              })`}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {step === "score" && (
        <div className="space-y-4 rounded-md border p-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">{t("matches.result")}</p>
            <div className="flex shrink-0 items-center gap-2">
              <Checkbox
                id="autoScore"
                checked={formData.autoScore}
                className={MODAL_CHECKBOX_CLASS}
                onCheckedChange={(checked) => {
                  if (checked) {
                    const teamAScore = formData.goalscorers?.filter((g) => g.team === "A").length || 0;
                    const teamBScore = formData.goalscorers?.filter((g) => g.team === "B").length || 0;
                    setFormData({
                      ...formData,
                      autoScore: true,
                      scoreA: teamAScore,
                      scoreB: teamBScore,
                      winner: undefined,
                    });
                  } else {
                    setFormData({ ...formData, autoScore: false });
                  }
                }}
              />
              <Label htmlFor="autoScore" className="text-sm">
                {t("matches.autoCalculate", "Auto-calculate from goalscorers")}
              </Label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="scoreA">{t("matches.teamAScore", "Team A Score (Optional)")}</Label>
              {formData.autoScore ? (
                <div className="mt-1 p-2 bg-muted rounded-md text-center font-medium">
                  {formData.scoreA || 0}
                </div>
              ) : (
                <Input
                  id="scoreA"
                  type="number"
                  min={0}
                  value={formData.scoreA}
                  onChange={(e) => setFormData({ ...formData, scoreA: e.target.value })}
                  className={cn("mt-1", MODAL_FIELD_CLASS)}
                  disabled={isProcessing}
                />
              )}
            </div>
            <div>
              <Label htmlFor="scoreB">{t("matches.teamBScore", "Team B Score (Optional)")}</Label>
              {formData.autoScore ? (
                <div className="mt-1 p-2 bg-muted rounded-md text-center font-medium">
                  {formData.scoreB || 0}
                </div>
              ) : (
                <Input
                  id="scoreB"
                  type="number"
                  min={0}
                  value={formData.scoreB}
                  onChange={(e) => setFormData({ ...formData, scoreB: e.target.value })}
                  className={cn("mt-1", MODAL_FIELD_CLASS)}
                  disabled={isProcessing}
                />
              )}
            </div>
          </div>
          {!formData.autoScore && (
            <div className="pt-4 border-t">
              <Label className="mb-2 block">{t("matches.matchResult", "Match Result")}:</Label>
              <RadioGroup
                value={formData.winner || ""}
                onValueChange={(value) =>
                  setFormData({ ...formData, winner: value as "A" | "B" | "Draw" })
                }
                className="flex flex-wrap gap-3"
                disabled={isProcessing}
              >
                <div className="flex min-h-9 items-center gap-2">
                  <RadioGroupItem value="A" id="r1" className={MODAL_CHECKBOX_CLASS} />
                  <Label htmlFor="r1" className="font-normal">
                    {t("matches.teamAWon")}
                  </Label>
                </div>
                <div className="flex min-h-9 items-center gap-2">
                  <RadioGroupItem value="B" id="r2" className={MODAL_CHECKBOX_CLASS} />
                  <Label htmlFor="r2" className="font-normal">
                    {t("matches.teamBWon")}
                  </Label>
                </div>
                <div className="flex min-h-9 items-center gap-2">
                  <RadioGroupItem value="Draw" id="r3" className={MODAL_CHECKBOX_CLASS} />
                  <Label htmlFor="r3" className="font-normal">
                    {t("matches.draw")}
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}
          <div className="space-y-3 border-t pt-4">
            <Label className="font-medium">{t("matches.goalscorers", "Goalscorers (Optional)")}</Label>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {(["A", "B"] as const).map((team) => {
                const teamKey = team === "A" ? "teamA" : "teamB";
                const teamPlayers = allSelectablePlayers.filter((p) =>
                  formData[teamKey].includes(p.id)
                );
                return (
                  <div key={team} className="min-w-0 space-y-1">
                    <p className="text-sm font-semibold">
                      {team === "A" ? t("matches.teamA") : t("matches.teamB")}
                    </p>
                    {teamPlayers.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        {t("matches.selectPlayersFirst", "Select players first")}
                      </p>
                    )}
                    <div className="max-h-[160px] space-y-1 overflow-y-auto">
                      {teamPlayers.map((player) => {
                        const count = getGoalscorerCount(team, player.id);
                        return (
                          <div
                            key={`goal-${team}-${player.id}`}
                            className="flex min-h-8 items-center justify-between gap-2"
                          >
                            <Label className="min-w-0 truncate text-sm font-normal">
                              {player.name}
                            </Label>
                            <div className="flex shrink-0 items-center gap-1.5">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 w-7 shrink-0 p-0 active:scale-100"
                                onClick={() => handleGoalscorerChange(team, player.id, false)}
                                disabled={count === 0 || isProcessing}
                              >
                                -
                              </Button>
                              <span className="w-4 shrink-0 text-center text-sm tabular-nums">
                                {count}
                              </span>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 w-7 shrink-0 p-0 active:scale-100"
                                onClick={() => handleGoalscorerChange(team, player.id, true)}
                                disabled={isProcessing}
                              >
                                +
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {step === "extras" && (
        <div className="space-y-4">
          <Label htmlFor="youtubeLink">{t("matches.youtubeLink")}</Label>
          <Input
            id="youtubeLink"
            type="url"
            placeholder={t("matches.youtubeLinkPlaceholder", "Optional (e.g., https://youtu.be/...)")}
            value={formData.youtubeLink}
            onChange={(e) => setFormData({ ...formData, youtubeLink: e.target.value })}
            disabled={isProcessing}
            className={MODAL_FIELD_CLASS}
          />
        </div>
      )}
    </div>
  );

  const footer = (
    <div className="flex justify-between gap-2 w-full">
      <Button
        type="button"
        variant="outline"
        onClick={() => (stepIndex === 0 ? onOpenChange(false) : goBack())}
        disabled={isProcessing}
      >
        {stepIndex === 0 ? t("common.cancel") : t("common.back", "Back")}
      </Button>
      {step === "extras" ? (
        <Button
          type="button"
          className="bg-soccer-primary hover:bg-soccer-primary/90"
          onClick={handleSubmit}
          disabled={isProcessing || !isFormValid}
        >
          {isProcessing && <FaSpinner className="mr-2 h-4 w-4 animate-spin" />}
          {mode === "edit" ? t("matches.saveChanges", "Save Changes") : t("matches.addMatch")}
        </Button>
      ) : (
        <Button
          type="button"
          className="bg-soccer-primary hover:bg-soccer-primary/90"
          onClick={goNext}
          disabled={isProcessing || !isStepValid(step)}
        >
          {t("common.next", "Next")}
        </Button>
      )}
    </div>
  );

  const title = mode === "edit" ? t("matches.editMatch") : t("matches.addMatch");

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="flex h-[90vh] max-h-[90vh] flex-col overflow-hidden rounded-t-xl">
          <SheetHeader className="shrink-0">
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
          <div className="shrink-0">{stepIndicator}</div>
          {stepContent}
          <SheetFooter className="shrink-0 pt-4">{footer}</SheetFooter>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,720px)] flex-col overflow-hidden sm:max-w-[650px]">
        <DialogHeader className="shrink-0">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="shrink-0">{stepIndicator}</div>
        {stepContent}
        <DialogFooter className="shrink-0 border-t pt-4">{footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MatchFormWizard;
